import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import { delayedRetry } from 'src/app/operators';
import { ApplicationInsightsService } from 'src/app/services/application-insights.service';

import { Feature, FeatureFlag } from '../models';

@Injectable({ providedIn: 'root' })
export class FeatureManagementService {
  private _featureFlags: Map<Feature, boolean> = new Map<Feature, boolean>();
  private _inFlightFlags$: Observable<Map<Feature, boolean>> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly applicationInsightsService: ApplicationInsightsService
  ) {}

  getIsFeatureFlagEnabled(featureName: Feature): Observable<boolean> {
    return this.http.get<FeatureFlag>(`featureMangagement/${featureName}`).pipe(
      map((data) => {
        return data.isEnabled;
      }),
      delayedRetry(1000, 2),
      catchError((err) => {
        return of(false);
      })
    );
  }

  /**
   * @deprecated Use {@link isFeatureEnabled$} instead. This method returns `undefined`
   * on a cache miss — its return statements sit inside the subscribe callback, so they
   * return from the callback rather than the method. Callers cannot distinguish
   * "flag is off" from "flags were never loaded". Tracked by bug 296592.
   */
  getIsFeatureEnabledWithCaching(featureName: Feature): boolean {
    if (this._featureFlags.size > 0 && this._featureFlags.has(featureName)) {
      return this._featureFlags.get(featureName);
    }

    this.getFeatureFlags().subscribe(
      (data: Map<Feature, boolean>) => {
        this._featureFlags = data;

        if (!this._featureFlags.has(featureName)) {
          this.applicationInsightsService.logEvent('FEATURE_MANAGEMENT_GET_ENABLED', {
            featureName: featureName,
          });
          return false;
        }

        return data.get(featureName);
      },
      (err) => {
        return of(false);
      }
    );
  }

  // Observable-based evaluation that is reliable on the first check after page load.
  // getIsFeatureEnabledWithCaching cannot be trusted on a cache miss (its returns sit
  // inside the subscribe callback), so kill-switch style flags must use this instead.
  isFeatureEnabled$(featureName: Feature): Observable<boolean> {
    if (this._featureFlags.has(featureName)) {
      return of(this._featureFlags.get(featureName));
    }

    // Share one fetch across every caller that arrives while the cache is cold,
    // so N components asking at startup produce one request instead of N.
    if (!this._inFlightFlags$) {
      this._inFlightFlags$ = this.getFeatureFlags().pipe(
        finalize(() => (this._inFlightFlags$ = null)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this._inFlightFlags$.pipe(map((flags) => flags.get(featureName) ?? false));
  }

  getFeatureFlags(): Observable<Map<Feature, boolean>> {
    return this.http.get<FeatureFlag[]>(`featureMangagement`).pipe(
      map((featureFlagArray) => {
        const featureMap = new Map<Feature, boolean>();
        featureFlagArray.forEach(function (flag) {
          featureMap.set(flag.name, flag.isEnabled);
        });
        this._featureFlags = featureMap;
        return featureMap;
      }),
      delayedRetry(1000, 2),
      catchError((err) => {
        this._featureFlags = new Map<Feature, boolean>();
        return of(this._featureFlags);
      })
    );
  }
}
